import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/api.ts';
import {
    Users,
    Bot,
    MessageSquare,
    Image,
    TrendingUp,
    Eye,
    Heart,
    RefreshCw,
    AlertCircle,
    CheckCircle,
    Clock
} from 'lucide-react';

interface Stats {
    users: {
        total: number;
        active: number;
        scheduledForDeletion: number;
    };
    characters: {
        total: number;
        public: number;
    };
    content: {
        chats: number;
        comments: number;
    };
    images: {
        usersWithCloudinary: number;
        usersWithBase64: number;
        migrationNeeded: number;
    };
    subscriptions: {
        [key: string]: number;
    };
}

const StatCard: React.FC<{
    icon: React.ReactNode;
    label: string;
    value: number | string;
    sublabel?: string;
    color: string;
    trend?: string;
}> = ({ icon, label, value, sublabel, color, trend }) => (
    <div style={{
        background: 'rgba(255,255,255,0.05)',
        backdropFilter: 'blur(20px)',
        borderRadius: '20px',
        padding: '24px',
        border: '1px solid rgba(255,255,255,0.1)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
    }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '14px',
                background: `linear-gradient(135deg, ${color}33, ${color}11)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: color
            }}>
                {icon}
            </div>
            {trend && (
                <span style={{
                    fontSize: '12px',
                    color: '#4ecdc4',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                }}>
                    <TrendingUp size={14} />
                    {trend}
                </span>
            )}
        </div>
        <div style={{ marginTop: '16px' }}>
            <p style={{
                color: 'rgba(255,255,255,0.5)',
                fontSize: '14px',
                marginBottom: '4px'
            }}>
                {label}
            </p>
            <p style={{
                color: 'white',
                fontSize: '32px',
                fontWeight: '700',
                margin: 0
            }}>
                {typeof value === 'number' ? value.toLocaleString() : value}
            </p>
            {sublabel && (
                <p style={{
                    color: 'rgba(255,255,255,0.4)',
                    fontSize: '12px',
                    marginTop: '4px'
                }}>
                    {sublabel}
                </p>
            )}
        </div>
    </div>
);

const AdminDashboard: React.FC = () => {
    const [stats, setStats] = useState<Stats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchStats = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/admin/stats`, {
                credentials: 'include'
            });
            const data = await response.json();

            if (data.success) {
                setStats(data.stats);
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Error loading stats');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, []);

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '50vh',
                color: 'white'
            }}>
                <RefreshCw size={32} className="spin" style={{ animation: 'spin 1s linear infinite' }} />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                background: 'rgba(255, 107, 107, 0.1)',
                border: '1px solid rgba(255, 107, 107, 0.3)',
                borderRadius: '16px',
                padding: '24px',
                color: '#ff6b6b',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
            }}>
                <AlertCircle size={24} />
                {error}
            </div>
        );
    }

    if (!stats) return null;

    return (
        <div>
            {/* Header */}
            <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h1 style={{ color: 'white', fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
                        Dashboard
                    </h1>
                    <p style={{ color: 'rgba(255,255,255,0.5)' }}>
                        Resumen general del sistema
                    </p>
                </div>
                <button
                    onClick={fetchStats}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 20px',
                        background: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '12px',
                        color: 'white',
                        cursor: 'pointer'
                    }}
                >
                    <RefreshCw size={18} />
                    Actualizar
                </button>
            </div>

            {/* Main Stats Grid */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: '20px',
                marginBottom: '32px'
            }}>
                <StatCard
                    icon={<Users size={24} />}
                    label="Usuarios Totales"
                    value={stats.users.total}
                    sublabel={`${stats.users.active} activos`}
                    color="#4ecdc4"
                />
                <StatCard
                    icon={<Bot size={24} />}
                    label="Personajes"
                    value={stats.characters.total}
                    sublabel={`${stats.characters.public} públicos`}
                    color="#ff6b6b"
                />
                <StatCard
                    icon={<MessageSquare size={24} />}
                    label="Chats"
                    value={stats.content.chats}
                    color="#ffd93d"
                />
                <StatCard
                    icon={<Eye size={24} />}
                    label="Comentarios"
                    value={stats.content.comments}
                    color="#a855f7"
                />
            </div>

            {/* Subscriptions & Images */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px'
            }}>
                {/* Subscriptions */}
                <div style={{
                    background: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '20px',
                    padding: '24px',
                    border: '1px solid rgba(255,255,255,0.1)'
                }}>
                    <h3 style={{
                        color: 'white',
                        fontSize: '18px',
                        fontWeight: '600',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <Heart size={20} style={{ color: '#ff6b6b' }} />
                        Suscripciones
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {Object.entries(stats.subscriptions).map(([tier, count]) => (
                            <div key={tier} style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '12px 16px',
                                background: 'rgba(0,0,0,0.2)',
                                borderRadius: '12px'
                            }}>
                                <span style={{ color: 'rgba(255,255,255,0.8)' }}>{tier}</span>
                                <span style={{
                                    color: 'white',
                                    fontWeight: '700',
                                    background: 'rgba(78, 205, 196, 0.2)',
                                    padding: '4px 12px',
                                    borderRadius: '8px'
                                }}>
                                    {count}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Image Migration Status */}
                <div style={{
                    background: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '20px',
                    padding: '24px',
                    border: '1px solid rgba(255,255,255,0.1)'
                }}>
                    <h3 style={{
                        color: 'white',
                        fontSize: '18px',
                        fontWeight: '600',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <Image size={20} style={{ color: '#4ecdc4' }} />
                        Estado de Imágenes
                    </h3>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '16px',
                            background: 'rgba(78, 205, 196, 0.1)',
                            borderRadius: '12px',
                            border: '1px solid rgba(78, 205, 196, 0.2)'
                        }}>
                            <CheckCircle size={24} style={{ color: '#4ecdc4' }} />
                            <div>
                                <p style={{ color: 'white', fontWeight: '600' }}>
                                    {stats.images.usersWithCloudinary} usuarios
                                </p>
                                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                                    Con avatares en Cloudinary
                                </p>
                            </div>
                        </div>

                        {stats.images.migrationNeeded > 0 ? (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '16px',
                                background: 'rgba(255, 217, 61, 0.1)',
                                borderRadius: '12px',
                                border: '1px solid rgba(255, 217, 61, 0.2)'
                            }}>
                                <Clock size={24} style={{ color: '#ffd93d' }} />
                                <div>
                                    <p style={{ color: 'white', fontWeight: '600' }}>
                                        {stats.images.migrationNeeded} pendientes
                                    </p>
                                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                                        Necesitan migración
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '16px',
                                background: 'rgba(78, 205, 196, 0.1)',
                                borderRadius: '12px',
                                border: '1px solid rgba(78, 205, 196, 0.2)'
                            }}>
                                <CheckCircle size={24} style={{ color: '#4ecdc4' }} />
                                <div>
                                    <p style={{ color: '#4ecdc4', fontWeight: '600' }}>
                                        ¡Migración Completa!
                                    </p>
                                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                                        Todas las imágenes están en Cloudinary
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
        </div>
    );
};

export default AdminDashboard;
